import React from 'react';
import { defineMessages, useIntl, type MessageValue, type NoMessageValues } from 'react-intl';
import { Text, StyleSheet, View } from 'react-native';

import { theme } from '@garnish/constants';
import { BodyText, TextSpace } from '@garnish/core';

// ────────────────────────────────────────────────────────────────────────────

export const HomeProductNutritionData = (props: HomeProductNutritionDataProps) => {
  const { calories, totalFatG, totalCarbsG, dietaryFiberG, sugarsG, proteinG } = props;

  const { formatMessage } = useIntl();

  return (
    <View style={styles.container}>
      {calories ? (
        <NutritionLabel value={calories} label={formatMessage(messages.calories)} inGrams={false} />
      ) : null}

      {totalFatG ? (
        <NutritionLabel value={totalFatG} label={formatMessage(messages.fat)} inGrams={true} />
      ) : null}

      {totalCarbsG ? (
        <NutritionLabel value={totalCarbsG} label={formatMessage(messages.carbs)} inGrams={true} />
      ) : null}

      {dietaryFiberG ? (
        <NutritionLabel
          value={dietaryFiberG}
          label={formatMessage(messages.fiber)}
          inGrams={true}
        />
      ) : null}

      {sugarsG ? (
        <NutritionLabel value={sugarsG} label={formatMessage(messages.sugars)} inGrams={true} />
      ) : null}

      {proteinG ? (
        <NutritionLabel value={proteinG} label={formatMessage(messages.protein)} inGrams={true} />
      ) : null}
    </View>
  );
};

const NutritionLabel = (props: NutritionLabelProps) => {
  const { value, label, inGrams } = props;

  const { formatMessage } = useIntl();

  return (
    <Text>
      <BodyText sizeMatch={['12']} bold>
        {inGrams ? formatMessage(messages.gramValue, { value }) : value}
      </BodyText>

      <TextSpace />

      <BodyText sizeMatch={['12']}>{label}</BodyText>
    </Text>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages<{
  readonly carbs: NoMessageValues;
  readonly protein: NoMessageValues;
  readonly fat: NoMessageValues;
  readonly calories: NoMessageValues;
  readonly sugars: NoMessageValues;
  readonly fiber: NoMessageValues;
  readonly gramValue: { readonly value: MessageValue };
}>({
  carbs: {
    defaultMessage: 'Carbs',
    description: 'Menu | Nutrition | Carbs | Label',
  },
  protein: {
    defaultMessage: 'Protein',
    description: 'Menu | Nutrition | Protein | Label',
  },
  fat: {
    defaultMessage: 'Fat',
    description: 'Menu | Nutrition | Fat | Label',
  },
  calories: {
    defaultMessage: 'Cal',
    description: 'Menu | Nutrition | Calories | Label',
  },
  sugars: {
    defaultMessage: 'Sugars',
    description: 'Menu | Nutrition | Sugars | Label',
  },
  fiber: {
    defaultMessage: 'Fiber',
    description: 'Menu | Nutrition | Fiber | Label',
  },
  gramValue: {
    defaultMessage: '{value}g',
    description: 'Menu | Nutrition | Gram value',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    columnGap: theme.spacing['4'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type HomeProductNutritionDataProps = {
  calories?: number;
  totalFatG?: number;
  totalCarbsG?: number;
  dietaryFiberG?: number;
  sugarsG?: number;
  proteinG?: number;
};

type NutritionLabelProps = {
  value: number;
  label: string;
  inGrams: boolean;
};
